import { useGetEncounters } from '@/api/get-encounters';
import { useState } from 'react';
import { TranslationFunction, useTranslation } from '@/contexts/i18n-context';
import { Button, ButtonVariant } from '@/components/button';
import styles from './dashboard-page.module.css';
import { PageContainer } from '@/components/containers/page-container';
import { useGetEncountersCurrentStatus } from '@/api/get-current-encounter-status';
import { DashboardStatus } from '@/api/api.schemas';
import { GetCurrentEncounterStatusResponse } from '@/api/api.schemas';
import { Layout } from '@/components/layout';
import { useGetEncountersWorkflow } from '@/api/get-encounter-workflow';
import {
  START_VISIT_DASHBOARD_STATUSES,
  VISIT_NOT_COMPLETED_DASHBOARD_STATUSES,
  VISIT_SHOWN_AT_DASHBOARD,
} from '@/util/constants';
import { MarketingSpot } from '@/components/marketing-spot';
import { Loader } from '@/components/loaders/loader';
import { Navigate } from '@/util/react-router-dom';
import { useCreateEncountersCancel } from '@/api/cancel-encounter';
import { useResubmitEncounter } from '@/api/resubmit-encounter';
import { EmptyVisitsSection } from '@/components/dashboard/empty-visits-section';
import clsx from 'clsx';
import { useMediaQuery } from '@/hooks/use-media-query';
import { DashboardVisitCard } from '@/components/dashboard/dashboard-visit-card';
import { Section } from '@/components/containers/section';
import { DashboardGradientFormTitle } from '@/components/containers/dashboard-gradient-form-title';
import { MarketingNotificationsBanner } from '@/components/dashboard/marketing-notifications-banner';
import { IdVerificationAnnouncementBanner } from '@/components/dashboard/id-verification-announcement-banner';
import { useBranding } from '@/outlets/branding-outlet';
import { ButtonConfig, getDashboardConfig, isButtonConfigValid } from './dashboard-config';
import { LogoutButton } from '@/components/logout-button';

interface DashboardPageProps {
  currentEncounter: GetCurrentEncounterStatusResponse | undefined;
}

export function DashboardPage() {
  const { networkId } = useBranding();
  const { data, isFetching } = useGetEncountersCurrentStatus({ networkId });

  if (isFetching) {
    return <Loader type="large" />;
  }

  return (
    <Layout
      hideVisitButton={
        !data?.dashboardStatus || !START_VISIT_DASHBOARD_STATUSES.includes(data.dashboardStatus)
      }
    >
      <DashboardPageContent currentEncounter={data} />
    </Layout>
  );
}

function DashboardPageContent({ currentEncounter }: DashboardPageProps) {
  const { welcomeMessage } = useBranding();
  const isMobile = useMediaQuery('--screen-md');

  return (
    <PageContainer className={styles.pageContainer}>
      {welcomeMessage && !isMobile && (
        <span className={styles.welcomeMessage}>{welcomeMessage}</span>
      )}
      {currentEncounter?.showIdVerificationAnnouncementBanner && (
        <IdVerificationAnnouncementBanner />
      )}
      {currentEncounter && <DashboardTitle currentEncounter={currentEncounter} />}

      <DashboardEncounterList currentEncounter={currentEncounter} />

      <MarketingSpot page="dashboard" spot="bottom" className={styles.marketingContent} />

      <MarketingNotificationsBanner />
    </PageContainer>
  );
}

function DashboardEncounterList({ currentEncounter }: DashboardPageProps) {
  const { t } = useTranslation();
  const { data, isFetching } = useGetEncounters({ status: 'Completed' });
  const [showAllVisits, setShowAllVisits] = useState(false);

  if (isFetching) {
    return (
      <Section>
        <Loader type="small" />
      </Section>
    );
  }

  if (data?.encounters.length === 0) {
    return (
      <EmptyVisitsSection
        showStartVisit={
          currentEncounter?.dashboardStatus === DashboardStatus.ProfileCompleteNoPrevVisit
        }
      />
    );
  }

  return (
    <>
      <h2 className={clsx('h4', styles.title)}>{t('dashboard.history.title')}</h2>
      <div className={styles.visitsContainer}>
        <div>
          {(showAllVisits
            ? data?.encounters
            : data?.encounters.slice(0, VISIT_SHOWN_AT_DASHBOARD)
          )?.map((encounter) => (
            <DashboardVisitCard key={encounter.id} encounter={encounter} />
          ))}
        </div>

        {data?.encounters && data?.encounters.length > VISIT_SHOWN_AT_DASHBOARD && (
          <Button
            variant="link"
            color="accent"
            onClick={() => setShowAllVisits(!showAllVisits)}
            rightIcon={showAllVisits ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            className={styles.viewVisitsButton}
          >
            {showAllVisits ? t('dashboard.history.showLess') : t('dashboard.history.viewAll')}
          </Button>
        )}
      </div>
    </>
  );
}

interface DashboardTitleProps {
  currentEncounter: GetCurrentEncounterStatusResponse;
}

function DashboardTitle({ currentEncounter }: DashboardTitleProps) {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('--screen-md');
  const { networkId } = useBranding();
  const { data: encounterWorkflow, isFetching } = useGetEncountersWorkflow(
    currentEncounter.encounterId ?? 0,
    {
      query: {
        enabled: VISIT_NOT_COMPLETED_DASHBOARD_STATUSES.includes(currentEncounter.dashboardStatus),
      },
    },
  );

  const { mutate: cancelEncounter } = useCreateEncountersCancel();
  const { mutate: resubmitEncounter, isPending: isResubmittingEncounter } = useResubmitEncounter();
  const { refetch } = useGetEncountersCurrentStatus(
    { networkId },
    { query: { refetchOnMount: false } },
  );

  function handleResubmitEncounter() {
    if (!currentEncounter.encounterId) {
      return;
    }

    resubmitEncounter(
      { encounterId: currentEncounter.encounterId },
      { onSuccess: () => refetch() },
    );
  }

  function handleCancelEncounter() {
    if (!currentEncounter.encounterId) {
      return;
    }

    cancelEncounter(
      { encounterId: currentEncounter.encounterId, data: {} },
      { onSuccess: () => refetch() },
    );
  }

  if (isFetching) {
    return <Loader type="large" />;
  }

  const stateConfig = getDashboardConfig({
    currentEncounter,
    onCancelEncounter: handleCancelEncounter,
    onResubmitEncounter: handleResubmitEncounter,
    isResubmittingEncounter,
    encounterWorkflow,
  });

  const { Title, Description, primaryButton, secondaryButton } = stateConfig;

  return (
    <DashboardGradientFormTitle
      withParagraphMargins={false}
      title={
        <div className={styles.dashboardDetails}>
          <h2 className={clsx('h3', styles.whiteText)}>{<Title t={t} />}</h2>
        </div>
      }
      body={
        <div className={styles.statusContent}>
          <p className={clsx(styles.description, isMobile ? 'semibold' : 'body-1')}>
            {<Description t={t} />}
          </p>
          <div className={styles.visitButtons}>
            {primaryButton && <DashboardActionButton t={t} config={primaryButton} />}
            {secondaryButton && (
              <DashboardActionButton t={t} config={secondaryButton} variant="outlined" />
            )}
          </div>
        </div>
      }
    />
  );
}

function DashboardActionButton({
  t,
  config,
  variant,
}: {
  t: TranslationFunction;
  config: ButtonConfig;
  variant?: ButtonVariant;
}) {
  if (config.as === 'logout') {
    return <LogoutButton color="accent" variant={variant} />;
  }

  const { text, ...rest } = config;

  if (!isButtonConfigValid(rest)) {
    return <Navigate to="/system-error" />;
  }

  return (
    <Button color="accent" variant={variant} {...rest}>
      {t(text)}
    </Button>
  );
}
